import { Box, Typography } from '@mui/material';
import type { ReactElement, ReactNode } from 'react';

import type { AdminAuditEvent } from '@/admin/adminApi';
import { TechnicalId } from '@/admin/users/accountsUi';
import { Badge, Button, LoadingState } from '@/ui/kit';
import { fontStacks } from '@/ui/tokens';

import { DEFAULT_FILTERS, type AuditFilters } from './auditFilters';
import {
  actionLabel,
  describeActor,
  describeTarget,
  formatFull,
  formatTime,
  isKnownAction,
  parseConsultedQuery,
  readableMetadata,
  reasonOf,
  targetTypeLabel,
} from './auditPresentation';
import { useAuditEvents } from './useAuditEvents';

/**
 * Tiroir de detail d'un evenement (maquette P14, volet de droite).
 *
 * Il met en regard ce qu'il faut pour **reconstituer une intervention** : qui,
 * quand, sur quoi, le **motif** saisi, les metadonnees telles qu'ecrites, et les
 * autres lignes ecrites par la meme requete (meme corrélation). Les gestes
 * « Filtrer sur… » sont ici, et pas dans un menu de la barre de filtres : on
 * choisit un acteur, une cible ou une corrélation **depuis une ligne**, la ou
 * on les lit — le serveur n'offre pas de liste d'administrateurs ou l'on
 * choisirait.
 */

/** Lignes de la meme requete : le serveur plafonne une page a 100. */
const CORRELATION_PAGE_SIZE = 100;

const Row = ({ label, children }: { readonly label: string; readonly children: ReactNode }): ReactElement => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(88px, 34%) minmax(0, 1fr)',
      gap: 1.5,
      py: 1,
      borderBottom: 1,
      borderColor: 'tokens.border',
      '&:last-of-type': { borderBottom: 0 },
    }}
  >
    <Typography component="dt" sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
      {label}
    </Typography>
    <Typography component="dd" sx={{ m: 0, fontSize: 14, fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>
      {children}
    </Typography>
  </Box>
);

const SectionTitle = ({ children }: { readonly children: ReactNode }): ReactElement => (
  <Typography component="h3" sx={{ fontSize: 15, fontWeight: 700, mt: 2.5, mb: 1 }}>
    {children}
  </Typography>
);

const Absent = (): ReactElement => <Box component="span" sx={{ color: 'tokens.textMuted', fontWeight: 400 }}>—</Box>;

/** Valeur lue, puis son identifiant complet au second rang (copiable). */
const WithId = ({
  label,
  id,
  idLabel,
}: {
  readonly label: ReactNode;
  readonly id: string | null;
  readonly idLabel: string;
}): ReactElement => (
  <>
    {label}
    {id === null ? null : <TechnicalId value={id} label={idLabel} />}
  </>
);

/** Paramètres lus d'une consultation, ou la requête brute d'une ligne antérieure. */
const ConsultedParameters = ({ event }: { readonly event: AdminAuditEvent }): ReactElement | null => {
  if (event.action !== 'admin.access') {
    return null;
  }

  const consulted = parseConsultedQuery(event.metadata);

  if (consulted.kind === 'none') {
    return (
      <>
        <SectionTitle>Paramètres de la consultation</SectionTitle>
        <Typography sx={{ fontSize: 14, color: 'tokens.textMuted' }}>Aucun paramètre.</Typography>
      </>
    );
  }

  return (
    <>
      <SectionTitle>Paramètres de la consultation</SectionTitle>
      {consulted.kind === 'raw' ? (
        <>
          <Typography sx={{ fontSize: 13, color: 'tokens.textMuted', mb: 0.5 }}>
            Ligne écrite avant que la garde ne filtre les paramètres : la requête est montrée telle qu’elle a été
            enregistrée.
          </Typography>
          <Typography sx={{ fontSize: 13, fontFamily: fontStacks.mono, overflowWrap: 'anywhere' }}>
            {consulted.text}
          </Typography>
        </>
      ) : (
        <Box component="dl" sx={{ m: 0 }}>
          {consulted.parameters.map((parameter) => (
            <Row key={parameter.label} label={parameter.label}>
              {parameter.value}
            </Row>
          ))}
        </Box>
      )}
    </>
  );
};

/** Autres lignes ecrites par la meme requete. */
const SameCorrelation = ({
  event,
  attempt,
}: {
  readonly event: AdminAuditEvent;
  readonly attempt: number;
}): ReactElement => {
  const correlationId = event.correlationId;
  const { busy, page, error } = useAuditEvents({
    enabled: correlationId !== null,
    filters: { ...DEFAULT_FILTERS, period: 'all', correlationId: correlationId ?? '' },
    page: 1,
    perPage: CORRELATION_PAGE_SIZE,
    attempt,
  });

  if (correlationId === null) {
    return (
      <>
        <SectionTitle>Même corrélation</SectionTitle>
        <Typography sx={{ fontSize: 14, color: 'tokens.textMuted' }}>
          Aucune corrélation n’est enregistrée pour cette ligne : elle a été écrite hors requête (console) ou avant
          l’introduction des identifiants de corrélation.
        </Typography>
      </>
    );
  }

  return (
    <>
      <SectionTitle>
        {page === null || page.total < 0 ? 'Même corrélation' : `Même corrélation · ${String(page.total)}`}
      </SectionTitle>
      {busy && page === null ? <LoadingState label="Chargement des événements liés…" /> : null}
      {error === null ? null : (
        <Typography role="alert" sx={{ fontSize: 14, color: 'error.main' }}>
          {error.message}
        </Typography>
      )}
      {page === null ? null : (
        <Box component="ul" aria-label="Événements de la même corrélation" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {page.items.map((related) => (
            <Box
              component="li"
              key={related.id}
              sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, py: 1, borderBottom: 1, borderColor: 'tokens.border', '&:last-of-type': { borderBottom: 0 } }}
            >
              <Typography sx={{ fontSize: 14, minWidth: 0, overflowWrap: 'anywhere' }}>
                {actionLabel(related.action)}
                {related.id === event.id ? (
                  <Box component="span" sx={{ ml: 1 }}>
                    <Badge label="Cet événement" tone="neutral" />
                  </Box>
                ) : null}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, flexShrink: 0 }}>
                {formatTime(related.occurredAt) ?? '—'}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </>
  );
};

export interface AuditEventDetailProps {
  readonly event: AdminAuditEvent;
  /** Tentative de chargement de l'ecran : « Actualiser » relit aussi les lignes liees. */
  readonly attempt: number;
  /** Pose un filtre et referme le tiroir (la liste change, la ligne peut en sortir). */
  readonly onFilter: (patch: Partial<AuditFilters>) => void;
}

export const AuditEventDetail = ({ event, attempt, onFilter }: AuditEventDetailProps): ReactElement => {
  const actor = describeActor(event);
  const target = describeTarget(event);
  const reason = reasonOf(event.metadata);
  const known = isKnownAction(event.action);
  const canFilterTarget = event.targetType !== null && event.targetId !== null;

  return (
    <Box>
      <Box component="header" sx={{ mb: 1 }}>
        <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700, lineHeight: '24px', overflowWrap: 'anywhere' }}>
          {actionLabel(event.action)}
        </Typography>
        {known ? (
          <Typography sx={{ fontSize: 13, fontFamily: fontStacks.mono, color: 'tokens.textMuted', overflowWrap: 'anywhere' }}>
            {event.action}
          </Typography>
        ) : null}
      </Box>

      <Box component="dl" sx={{ m: 0 }}>
        <Row label="Quand">{formatFull(event.occurredAt) ?? <Absent />}</Row>
        <Row label="Acteur">
          <WithId
            label={actor.label}
            id={event.actorUserId}
            idLabel="l’identifiant de l’acteur"
          />
        </Row>
        <Row label="Cible">
          {event.targetType !== null && event.targetType !== 'route' && target.label !== targetTypeLabel(event.targetType) ? (
            <Typography component="span" sx={{ display: 'block', fontSize: 12, fontWeight: 400, color: 'tokens.textMuted' }}>
              {targetTypeLabel(event.targetType)}
            </Typography>
          ) : null}
          {target.label === null ? (
            <Absent />
          ) : (
            <WithId label={target.label} id={event.targetId} idLabel="l’identifiant de la cible" />
          )}
        </Row>
        <Row label="Motif">{reason ?? <Absent />}</Row>
        <Row label="Corrélation">
          {event.correlationId === null ? (
            <Absent />
          ) : (
            <TechnicalId value={event.correlationId} label="l’identifiant de corrélation" />
          )}
        </Row>
        {event.babyProfileId === null ? null : (
          <Row label="Profil bébé">
            <TechnicalId value={event.babyProfileId} label="l’identifiant du profil bébé" />
          </Row>
        )}
        <Row label="Événement">
          <TechnicalId value={event.id} label="l’identifiant de l’événement" />
        </Row>
      </Box>

      <SectionTitle>Filtrer le journal</SectionTitle>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {event.actorUserId === null ? null : (
          <Button
            size="S"
            onClick={() => {
              onFilter({ actorUserId: event.actorUserId ?? '' });
            }}
          >
            Filtrer sur cet acteur
          </Button>
        )}
        {canFilterTarget ? (
          <Button
            size="S"
            onClick={() => {
              onFilter({ targetType: event.targetType ?? '', targetId: event.targetId ?? '' });
            }}
          >
            Filtrer sur cette cible
          </Button>
        ) : null}
        {event.correlationId === null ? null : (
          <Button
            size="S"
            onClick={() => {
              onFilter({ correlationId: event.correlationId ?? '' });
            }}
          >
            Filtrer sur cette corrélation
          </Button>
        )}
        <Button
          size="S"
          onClick={() => {
            onFilter({ action: event.action });
          }}
        >
          Filtrer sur cette action
        </Button>
      </Box>

      <ConsultedParameters event={event} />

      <SectionTitle>Métadonnées</SectionTitle>
      <Box
        component="pre"
        tabIndex={0}
        aria-label="Métadonnées de l’événement, au format JSON"
        sx={{
          m: 0,
          p: 1.5,
          borderRadius: '12px',
          bgcolor: 'tokens.surfaceMuted',
          fontFamily: fontStacks.mono,
          fontSize: 12,
          lineHeight: '18px',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          overflowX: 'auto',
          '&:focus-visible': { outline: '2px solid', outlineColor: 'tokens.primary', outlineOffset: 2 },
        }}
      >
        {JSON.stringify(readableMetadata(event.metadata), null, 2)}
      </Box>

      <SameCorrelation event={event} attempt={attempt} />
    </Box>
  );
};
